import { ReactNode } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { Layout, Menu, MenuTheme, Typography } from 'antd';

import { Role } from '@app/domain-interfaces';

import { useUserAuth } from '../auth';
import { useToken } from '../theme';

export interface NavMenuItemType {
  key: string;
  label: ReactNode;
  roles?: Role[];
  children?: NavMenuItemType[];
  theme?: MenuTheme;
}

export type HeaderProps = {
  logoUrl: string;
  appName?: string;
  items: NavMenuItemType[];
  environment?: string;
  showEnvironment?: boolean;
  disableLogoLink?: boolean;
};

export function Header({
  logoUrl,
  appName = 'SAAF',
  items,
  environment,
  showEnvironment,
  disableLogoLink,
}: HeaderProps) {
  const location = useLocation();
  const { user } = useUserAuth();
  const {
    token: { rust },
  } = useToken();

  const shouldIncludeNavItem = (navItem: NavMenuItemType) => {
    // include nav item if no roles specified. If specified, check if user has at least one of the required roles
    return (
      !navItem.roles ||
      navItem.roles.some((role: Role) => (navItem.key === 'system-admin' && user?.isSystemAdmin) || user?.roles?.[role])
    );
  };

  const filterNavItems = (
    items: NavMenuItemType[],
    criteria: (value: NavMenuItemType) => boolean,
  ): NavMenuItemType[] => {
    return items.reduce<NavMenuItemType[]>((acc, item) => {
      if (!item.children) {
        if (criteria(item)) {
          acc = [...acc, item];
        }
      } else {
        const filteredChildren = filterNavItems(item.children, criteria);
        if (filteredChildren.length > 0) {
          acc = [...acc, { ...item, children: filteredChildren }];
        }
      }
      return acc;
    }, []);
  };

  const filteredItems = filterNavItems(items, (value) => shouldIncludeNavItem(value));

  const selectedKeys = filteredItems
    .filter((x) => matchPath(x?.key + '/*', location.pathname))
    .map((x) => x?.key?.toString() || '');

  return (
    <>
      <Layout.Header>
        {!disableLogoLink ? (
          <Link to="/">
            <img src={logoUrl} alt="company logo" style={{ float: 'left', width: 64, height: 64, marginRight: 30 }} />
            <Typography
              style={{
                float: 'left',
                color: 'white',
                lineHeight: '64px',
                marginRight: 45,
                fontSize: 24,
              }}
            >
              {appName}
            </Typography>
          </Link>
        ) : (
          <>
            <img src={logoUrl} alt="company logo" style={{ float: 'left', width: 64, height: 64, marginRight: 30 }} />
            <Typography
              style={{
                float: 'left',
                color: 'white',
                lineHeight: '64px',
                marginRight: 45,
                fontSize: 24,
              }}
            >
              {appName}
            </Typography>
          </>
        )}

        <Menu theme="dark" mode="horizontal" selectedKeys={selectedKeys} items={filteredItems} />
      </Layout.Header>
      {showEnvironment && (
        <div
          style={{
            paddingInline: 50,
            paddingBlock: 1,
            backgroundColor: rust,
            color: 'white',
          }}
        >
          {environment} Environment
        </div>
      )}
    </>
  );
}

export default Header;
